import { supabase } from "@/integrations/supabase/client";

export interface PurchaseHistoryItem {
  id: string;
  date: string;
  total: number;
  status: "completed" | "pending" | "failed";
  paymentMethod: "credit-card" | "apple-pay" | "google-pay";
  promoCode: string | null;
  promoDiscount: number;
  creditApplied: number;
  shippingCost: number;
  subtotal: number;
  taxAmount: number;
  promoEligibleItems: any[];
  items: {
    productId: number;
    name: string;
    quantity: number;
    price: number;
  }[];
}

export const fetchPurchaseHistory = async (userId: string): Promise<PurchaseHistoryItem[]> => {
  try {
    console.log(`Fetching purchase history for user: ${userId}`);
    
    if (!userId) {
      console.warn('No user ID provided for purchase history fetch');
      return [];
    }

    // Check current auth session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    console.log('Current auth session when fetching purchase history:', { 
      hasSession: !!session, 
      sessionUserId: session?.user?.id,
      requestedUserId: userId,
      sessionError 
    });

    const { data: purchases, error } = await supabase
      .from('purchase_history')
      .select(`
        *,
        purchase_items (
          product_id,
          name,
          quantity,
          price
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    console.log('Purchase history query result:', { purchases, error, userId });

    if (error) {
      console.error('Error fetching purchase history:', error);
      return [];
    }

    if (!purchases || purchases.length === 0) {
      console.log('No purchase history found');
      return [];
    }

    console.log(`Found ${purchases.length} purchase records`);

    const formattedPurchases: PurchaseHistoryItem[] = purchases.map(purchase => {
      // Safely handle metadata
      const metadata = purchase.metadata as { [key: string]: any } | null;
      
      return {
        id: purchase.id,
        date: purchase.created_at,
        total: purchase.total,
        status: purchase.status as "completed" | "pending" | "failed",
        paymentMethod: (purchase.payment_method as "credit-card" | "apple-pay" | "google-pay") || "credit-card",
        promoCode: metadata?.promo_code || null,
        promoDiscount: metadata?.promo_discount ? Number(metadata.promo_discount) : 0,
        creditApplied: metadata?.applied_credit ? Number(metadata.applied_credit) : 0,
        shippingCost: metadata?.shipping_cost ? Number(metadata.shipping_cost) : 0,
        subtotal: metadata?.subtotal ? Number(metadata.subtotal) : purchase.total,
        taxAmount: metadata?.tax_amount ? Number(metadata.tax_amount) : 0,
        promoEligibleItems: metadata?.promoEligibleItems || [],
        items: Array.isArray(purchase.purchase_items) ? purchase.purchase_items.map(item => ({
          productId: item.product_id,
          name: item.name,
          quantity: item.quantity,
          price: Number(item.price)
        })) : []
      };
    });

    console.log(`Formatted ${formattedPurchases.length} purchase history items`);
    return formattedPurchases;

  } catch (error) {
    console.error('Exception in fetchPurchaseHistory:', error);
    return [];
  }
};
